import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { FaSearch, FaLeaf, FaPills, FaFilter, FaDownload } from 'react-icons/fa';
import { toast } from 'sonner';
import { generateProductCataloguePDF } from '../utils/pdfGenerator';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedSubcategory, setSelectedSubcategory] = useState('All');

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    filterProducts();
  }, [products, searchQuery, selectedCategory, selectedSubcategory]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load products');
      setLoading(false);
    }
  };

  const filterProducts = () => {
    let filtered = [...products];

    if (selectedCategory !== 'All') {
      filtered = filtered.filter(p => p.category === selectedCategory);
    }

    if (selectedSubcategory !== 'All') {
      filtered = filtered.filter(p => p.subcategory === selectedSubcategory);
    }

    if (searchQuery) {
      filtered = filtered.filter(p =>
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.composition.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.benefits.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredProducts(filtered);
  };

  const categories = ['All', 'Herbal', 'Nutraceutical'];
  const subcategories = ['All', 'Syrup', 'Tablet', 'Chewable Tablet', 'Effervescent Tablet', 'Capsule', 'Oil', 'Juice', 'Powder', 'Sachet', 'Soap', 'Ointment', 'Health Supplement'];

  const getCategoryIcon = (category) => {
    return category === 'Herbal' ? FaLeaf : FaPills;
  };

  const getCategoryColor = (category) => {
    return category === 'Herbal' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800';
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="products-page">
      {/* Header */}
      <section className="bg-gradient-to-r from-[#0F5132] to-[#0F5132] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">Product Catalogue</h1>
              <p className="text-xl text-gray-100">Browse our complete range of Ayurvedic and Nutraceutical products</p>
            </div>
            <button
              onClick={() => {
                toast.info('Generating PDF...');
                setTimeout(() => generateProductCataloguePDF(products), 500);
              }}
              className="mt-6 md:mt-0 flex items-center space-x-2 px-8 py-4 bg-[#C9A227] hover:bg-[#B8921F] text-white font-bold rounded-lg transition-all duration-200 shadow-xl"
            >
              <FaDownload />
              <span>Download PDF Catalogue</span>
            </button>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Search and Filters */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="md:col-span-2 relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <Input
                type="text"
                placeholder="Search products..."
                data-testid="product-search-input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 w-full"
              />
            </div>

            {/* Category Filter */}
            <div>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                data-testid="category-filter"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F5132] focus:border-transparent"
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat} Products</option>
                ))}
              </select>
            </div>

            {/* Subcategory Filter */}
            <div>
              <select
                value={selectedSubcategory}
                onChange={(e) => setSelectedSubcategory(e.target.value)}
                data-testid="subcategory-filter"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F5132] focus:border-transparent"
              >
                {subcategories.map(sub => (
                  <option key={sub} value={sub}>{sub === 'All' ? 'All Types' : sub}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-6 text-gray-600">
          Showing <span className="font-semibold text-[#0F5132]">{filteredProducts.length}</span> of {products.length} products
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-[#0F5132] border-t-transparent"></div>
            <p className="mt-4 text-gray-600">Loading products...</p>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-20">
            <FaSearch className="mx-auto text-6xl text-gray-300 mb-4" />
            <h3 className="text-2xl font-bold text-[#0F5132] mb-2">No products found</h3>
            <p className="text-gray-600">Try adjusting your search or filters</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProducts.map((product) => {
              const CategoryIcon = getCategoryIcon(product.category);
              return (
                <div
                  key={product.id}
                  data-testid={`product-card-${product.id}`}
                  className="bg-white rounded-xl shadow-md hover:shadow-2xl transition-all duration-300 overflow-hidden group hover:-translate-y-1"
                >
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className={`px-3 py-1 rounded-full text-xs font-semibold ${getCategoryColor(product.category)}`}>
                        {product.category}
                      </div>
                      <CategoryIcon className="text-[#0F5132] text-2xl" />
                    </div>

                    <h3 className="text-xl font-bold text-[#0F5132] mb-2 line-clamp-2">
                      {product.name}
                    </h3>

                    <div className="mb-3">
                      <span className="inline-block px-2 py-1 bg-gray-100 text-[#0F5132] text-xs rounded">
                        {product.subcategory}
                      </span>
                    </div>

                    <p className="text-sm text-gray-600 mb-4 line-clamp-3">
                      {product.benefits}
                    </p>

                    <Link
                      to={`/products/${product.id}`}
                      data-testid={`view-product-${product.id}`}
                      className="mt-4 block w-full text-center px-4 py-2.5 bg-[#0F5132] hover:bg-[#0D4228] text-white font-semibold rounded-lg transition-colors duration-200"
                    >
                      View Details
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductsPage;